import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [captured, setCaptured] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const capturePayment = async () => {
      const paymentId = searchParams.get('paymentId');
      const payerId = searchParams.get('PayerID');

      if (!paymentId || !payerId) {
        setError('Invalid payment information');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/paypal/capture-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ order_id: paymentId })
        });

        const data = await response.json();
        if (data.success) {
          setCaptured(true);
          toast.success('Payment completed successfully!');
        } else {
          setError(data.detail || 'Payment capture failed');
        }
      } catch (err) {
        console.error('Capture error:', err);
        setError('Failed to process payment. Please contact support.');
      } finally {
        setLoading(false);
      }
    };

    capturePayment();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#00AEEF] animate-spin mx-auto mb-4" />
          <p className="text-xl text-[#94A3B8]">Processing your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22]">
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="w-24 h-24 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-8">
            <XCircle className="w-12 h-12 text-red-500" />
          </div>
          <h1 className="text-4xl font-black text-white mb-4">Payment Issue</h1>
          <p className="text-xl text-[#94A3B8] mb-8">{error}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/donate"
              className="inline-flex items-center justify-center gap-2 bg-[#FF6A00] text-white font-bold rounded-xl px-8 py-4 hover:shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all"
            >
              Try Again
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#00AEEF] text-[#00AEEF] font-bold rounded-xl px-8 py-4 hover:bg-[#00AEEF]/10 transition-all"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="w-24 h-24 rounded-full bg-[#00FFE0]/20 flex items-center justify-center mx-auto mb-8">
          <CheckCircle className="w-12 h-12 text-[#00FFE0]" />
        </div>
        <h1 className="text-4xl font-black text-white mb-4">Thank You!</h1>
        <p className="text-xl text-[#94A3B8] mb-4">
          Your payment has been processed successfully.
        </p>
        <p className="text-[#94A3B8] mb-8">
          You&apos;re helping build the next generation of tech talent! A receipt will be sent to your email.
          As a 501(c)(3) nonprofit, your donation may be tax-deductible.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 bg-[#FF6A00] text-white font-bold rounded-xl px-8 py-4 hover:shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all"
          >
            Return Home
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to="/programs"
            className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#00AEEF] text-[#00AEEF] font-bold rounded-xl px-8 py-4 hover:bg-[#00AEEF]/10 transition-all"
          >
            View Programs
          </Link>
        </div>
      </div>
    </div>
  );
};

const PaymentCancel = () => {
  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="w-24 h-24 rounded-full bg-[#FF6A00]/20 flex items-center justify-center mx-auto mb-8">
          <XCircle className="w-12 h-12 text-[#FF6A00]" />
        </div>
        <h1 className="text-4xl font-black text-white mb-4">Payment Cancelled</h1>
        <p className="text-xl text-[#94A3B8] mb-8">
          Your payment was cancelled. No charges have been made.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/donate"
            className="inline-flex items-center justify-center gap-2 bg-[#FF6A00] text-white font-bold rounded-xl px-8 py-4 hover:shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all"
          >
            Try Again
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#00AEEF] text-[#00AEEF] font-bold rounded-xl px-8 py-4 hover:bg-[#00AEEF]/10 transition-all"
          >
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export { PaymentSuccess, PaymentCancel };
